import type {
  ApiBlock,
  BlockType,
} from '@backmarket/http-api/src/api-specs-content/models/block'
import type { CountdownBannerApiProps } from '@backmarket/http-api/src/api-specs-content/models/countdown-banner'
import type { EmailCollectorApiProps } from '@backmarket/http-api/src/api-specs-content/models/email-collector'
import type {
  SectionBannerApiProps,
  SectionBannerBackgroundApiProps,
} from '@backmarket/http-api/src/api-specs-content/models/section-banner'
import type { BackgroundProps } from '@backmarket/nuxt-layer-cms/Background.types'
import type { CountdownBannerBlockProps } from '@backmarket/nuxt-layer-cms/CountdownBanner.types'
import type { EmailCollectorBlockProps } from '@backmarket/nuxt-layer-cms/EmailCollector.types'
import type { SectionBannerBlockProps } from '@backmarket/nuxt-layer-cms/SectionBanner.types'
import { buildSimpleRichText } from '@backmarket/nuxt-layer-cms/buildSimpleRichText'
import { buildPropsWithCorrectLinks } from '@backmarket/nuxt-layer-cms/utils/buildPropsWithCorrectLinks'

function mapToBackground(
  props: SectionBannerBackgroundApiProps,
): BackgroundProps {
  if (
    props.sectionBannerImageBackground &&
    'image' in props.sectionBannerImageBackground
  ) {
    return {
      background: {
        image: props.sectionBannerImageBackground.image,
        mode: props.sectionBannerImageBackground.mode,
      },
    }
  }

  if (props.sectionBannerMonochromeBackground?.theme) {
    return {
      background: {
        theme: props.sectionBannerMonochromeBackground.theme,
      },
    }
  }

  return { background: undefined }
}

function mapBackgroundProps<
  ApiProps extends SectionBannerBackgroundApiProps,
  BlockProps,
>(apiProps: ApiProps): BlockProps {
  const {
    sectionBannerImageBackground,
    sectionBannerMonochromeBackground,
    ...rest
  } = apiProps

  const { background } = mapToBackground({
    sectionBannerImageBackground,
    sectionBannerMonochromeBackground,
  })

  return { ...rest, background } as BlockProps
}

function mapSectionBannerProps(
  apiProps: SectionBannerApiProps,
): SectionBannerBlockProps {
  const title =
    apiProps.titleV2 ||
    (apiProps.title ? buildSimpleRichText(apiProps.title, 'h2') : undefined)

  const description =
    apiProps.descriptionV2 ||
    (apiProps.description
      ? buildSimpleRichText(apiProps.description, 'p')
      : undefined)

  return {
    ...mapBackgroundProps<SectionBannerApiProps, SectionBannerBlockProps>(
      apiProps,
    ),
    title,
    description,
  }
}

// TODO: map BlockTypeProps to ApiProps
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Mapper = (params: any) => any
const blockPropsMappers: Partial<Record<BlockType, Mapper>> = {
  SectionBanner: mapSectionBannerProps,
  EmailCollector: mapBackgroundProps<
    EmailCollectorApiProps,
    EmailCollectorBlockProps
  >,
  UpcomingSaleCountdownBanner: mapBackgroundProps<
    CountdownBannerApiProps,
    CountdownBannerBlockProps
  >,
}

export function formatBlock(
  block: ApiBlock,
  currentUrl: URL,
  disablePromisePage = false,
) {
  const propsWithCorrectLinks = buildPropsWithCorrectLinks({
    props: block.props,
    currentUrl,
    disablePromisePage,
  })

  const blockMapper = blockPropsMappers[block.type]

  if (!blockMapper) return { ...block, props: propsWithCorrectLinks }

  return { ...block, props: blockMapper(propsWithCorrectLinks) }
}
